import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  IbanInvoiceAddCommand,
  IbanInvoiceApi,
  IbanInvoiceGetByFilterQuery,
  IbanInvoicePaginatedResponseDto,
  IbanInvoicePaymentResponseDto,
  IbanInvoiceResponseDto,
  IbanInvoiceUpdateCommand,
  IbanInvoiceUpdateFavoriteCommand,
  IbanInvoiceValidatedResponseDto,
} from "api";
import { isMobile } from "react-device-detect";

import { IbanKeys } from "shared/constants/banks";
import rowsPerPageService, { RowsPerPageKeys } from "services/rowsPerPage";

const ibanInvoiceApi = new IbanInvoiceApi();

export interface IbanInvoice extends IbanInvoiceResponseDto {
  id: string;
}

export interface IbanInvoicePayment extends IbanInvoicePaymentResponseDto {
  id: string;
}

export interface IbanInvoiceState {
  latestIbanInvoices: IbanInvoice[] | null; // TODO: Rewiew and remove - ?
  ibanInvoices: IbanInvoice[] | null;

  duplicateInvoice: IbanInvoice | null;
  pageCount: number;
  pageNumber: number;
  pageSize: number;
  totalCount: number;

  createdInvoiceUrl: string | null;
  showSuccessModal: boolean;

  selectedInvoice: IbanInvoice | null;
  showEditModal: boolean;
  showDeleteModal: boolean;
  showQRCodeModal: boolean;
}

const initialState: IbanInvoiceState = {
  latestIbanInvoices: null,
  ibanInvoices: null,

  duplicateInvoice: null,
  pageCount: 0, // total number of pages
  pageNumber: 1, // current page number
  pageSize: rowsPerPageService(RowsPerPageKeys.INVOICES).getRowsPerPage() ?? (isMobile ? 5 : 25), // number of items per page
  totalCount: 0, // total number of items

  createdInvoiceUrl: null,
  showSuccessModal: false,

  selectedInvoice: null,
  showEditModal: false,
  showDeleteModal: false,
  showQRCodeModal: false,
};

export const getLatestIbanInvoices =
  createAsyncThunk<IbanInvoicePaginatedResponseDto>(
    "ibanInvoice/getLatest",
    async () => {
      const { data } = await ibanInvoiceApi.apiIbanInvoiceGet(1, 5);

      return data;
    },
  );

export const getIbanInvoices = createAsyncThunk<
  IbanInvoicePaginatedResponseDto,
  {
    pageNumber?: number;
    pageSize?: number;
  }
>("ibanInvoice/getAll", async ({ pageNumber, pageSize }, thunkAPI) => {
  const state = thunkAPI.getState() as { ibanInvoice: IbanInvoiceState };

  pageNumber = pageNumber ?? state.ibanInvoice.pageNumber;

  const { data } = await ibanInvoiceApi.apiIbanInvoiceGet(pageNumber, pageSize);

  return data;
});

export const searchIbanInvoices = createAsyncThunk<
  IbanInvoicePaginatedResponseDto,
  IbanInvoiceGetByFilterQuery
>(
  "ibanInvoice/search",
  async ({ pageNumber, pageSize, ...filters }, thunkAPI) => {
    const state = thunkAPI.getState() as { ibanInvoice: IbanInvoiceState };

    pageNumber = pageNumber ?? state.ibanInvoice.pageNumber;

    const { data } = await ibanInvoiceApi.apiIbanInvoiceSearchPost({
      pageNumber,
      pageSize,
      ...filters,
    });

    return data;
  },
);

export const getIbanInvoiceByUid = createAsyncThunk<
  IbanInvoiceResponseDto,
  string
>("ibanInvoice/getByUid", async (uid: string) => {
  const { data } = await ibanInvoiceApi.apiIbanInvoiceIbanInvoiceUidGet(uid);
  return data;
});

export const createIbanInvoice = createAsyncThunk<
  IbanInvoiceValidatedResponseDto,
  {
    body: IbanInvoiceAddCommand;
  }
>("ibanInvoice/create", async ({ body }) => {
  const { data } = await ibanInvoiceApi.apiIbanInvoicePost(body);

  data.ibanAccountUid &&
    localStorage.setItem(IbanKeys.PREFERRED_ACCOUNT_UID, data.ibanAccountUid);

  return data;
});

export const updateIbanInvoiceByUid = createAsyncThunk<
  IbanInvoiceResponseDto,
  {
    body: IbanInvoiceUpdateCommand;
  }
>("ibanInvoice/update", async ({ body }) => {
  const { data } = await ibanInvoiceApi.apiIbanInvoicePut(body);
  return data;
});

export const updateFavoriteIbanInvoiceByUid = createAsyncThunk<
  IbanInvoiceResponseDto,
  {
    body: IbanInvoiceUpdateFavoriteCommand;
  }
>("ibanInvoice/updateFavorite", async ({ body }) => {
  const { data } = await ibanInvoiceApi.apiIbanInvoiceFavoritePut(body);
  return data;
});

export const deleteIbanInvoiceByUid = createAsyncThunk<void, string>(
  "ibanInvoice/delete",
  async (uid: string) => {
    await ibanInvoiceApi.apiIbanInvoiceIbanInvoiceUidDelete(uid);
  },
);

const ibanInvoiceSlice = createSlice({
  name: "ibanInvoice",
  initialState,
  reducers: {
    setPageNumber: (state, action) => {
      state.pageNumber = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
      state.pageCount = Math.ceil(state.totalCount / action.payload);
      rowsPerPageService(RowsPerPageKeys.INVOICES).setRowsPerPage(action.payload);
    },
    setDuplicateInvoice: (state, action) => {
      state.duplicateInvoice = action.payload;
    },
    setCreatedInvoiceUrl: (state, action) => {
      state.createdInvoiceUrl = action.payload;
    },
    setShowSuccessModal: (state, action) => {
      state.showSuccessModal = action.payload;
    },
    setSelectedInvoice: (state, action) => {
      state.selectedInvoice = action.payload;
    },
    setShowEditModal: (state, action) => {
      state.showEditModal = action.payload;
    },
    setShowDeleteModal: (state, action) => {
      state.showDeleteModal = action.payload;
    },
    setShowQRCodeModal: (state, action) => {
      state.showQRCodeModal = action.payload;
    },
    setIbanInvoiceFavorite: (state, action) => {
      const invoice = state.ibanInvoices?.find(
        (i) => i.ibanQrInvoiceUid === action.payload.ibanQrInvoiceUid,
      );
      const latestInvoice = state.latestIbanInvoices?.find(
        (i) => i.ibanQrInvoiceUid === action.payload.ibanQrInvoiceUid,
      );

      if (invoice) {
        invoice.isFavorite = action.payload.isFavorite;
      }

      if (latestInvoice) {
        latestInvoice.isFavorite = action.payload.isFavorite;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(deleteIbanInvoiceByUid.fulfilled, (state) => {
      if (
        state.pageCount === state.pageNumber &&
        state.totalCount % state.pageSize === 1 &&
        state.pageNumber > 1
      ) {
        state.pageCount -= 1;
        state.pageNumber -= 1;
      }
      state.totalCount -= 1;
    });
    builder.addCase(createIbanInvoice.fulfilled, (state) => {
      state.totalCount += 1;

      state.pageNumber = 1;
    });
    builder.addCase(getLatestIbanInvoices.fulfilled, (state, action) => {
      return {
        ...state,
        latestIbanInvoices: action.payload.ibanInvoices.map((item) => ({
          ...item,
          id: item.ibanQrInvoiceUid,
        })),
        pageCount: action.payload.pageCount,
        totalCount: action.payload.totalCount,
        ibanInvoices: action.payload.ibanInvoices.map((item) => ({
          ...item,
          id: item.ibanQrInvoiceUid,
        })),
      };
    });
    builder.addCase(getIbanInvoices.fulfilled, (state, action) => {
      return {
        ...state,
        pageNumber: action.payload.pageNumber,
        pageSize: action.payload.pageSize,
        pageCount: action.payload.pageCount,
        totalCount: action.payload.totalCount,
        ibanInvoices: action.payload.ibanInvoices.map((item) => ({
          ...item,
          id: item.ibanQrInvoiceUid,
        })),
      };
    });
    builder.addCase(searchIbanInvoices.fulfilled, (state, action) => {
      return {
        ...state,
        pageNumber: action.payload.pageNumber,
        pageSize: action.payload.pageSize,
        pageCount: action.payload.pageCount,
        totalCount: action.payload.totalCount,
        ibanInvoices: action.payload.ibanInvoices.map((item) => ({
          ...item,
          id: item.ibanQrInvoiceUid,
        })),
      };
    });
  },
});

export const {
  setPageNumber,
  setPageSize,
  setIbanInvoiceFavorite,
  setDuplicateInvoice,
  setCreatedInvoiceUrl,
  setShowSuccessModal,
  setSelectedInvoice,
  setShowEditModal,
  setShowDeleteModal,
  setShowQRCodeModal,
} = ibanInvoiceSlice.actions;
export default ibanInvoiceSlice.reducer;
